import { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Gift, Copy, Trash2, Power, Sparkles, Plus, RefreshCw } from "lucide-react";
import api from "@/utils/api";

const copyToClipboard = (text) => {
  navigator.clipboard.writeText(text).then(() => toast.success(`Copied: ${text}`));
};

function PromoRow({ promo, onToggle, onDelete }) {
  const usage = promo.max_uses ? `${promo.used_count || 0} / ${promo.max_uses}` : `${promo.used_count || 0} / ∞`;
  const isExhausted = promo.max_uses && promo.used_count >= promo.max_uses;
  return (
    <tr className="border-t border-zinc-800/50 hover:bg-zinc-900/30" data-testid={`promo-row-${promo.code}`}>
      <td className="p-3">
        <div className="flex items-center gap-2">
          <code className="text-cyan-400 font-mono text-sm bg-zinc-900 px-2 py-1 rounded">{promo.code}</code>
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7 text-zinc-500 hover:text-white"
            onClick={() => copyToClipboard(promo.code)}
            data-testid={`copy-promo-${promo.code}`}
          >
            <Copy className="w-3 h-3" />
          </Button>
        </div>
      </td>
      <td className="p-3 text-zinc-300">{promo.trial_days}d</td>
      <td className="p-3">
        <span className={isExhausted ? "text-red-400" : "text-zinc-300"}>{usage}</span>
      </td>
      <td className="p-3">
        <Badge className={promo.active ? "bg-green-500/20 text-green-400" : "bg-zinc-700 text-zinc-400"}>
          {promo.active ? "ACTIVE" : "PAUSED"}
        </Badge>
      </td>
      <td className="p-3 text-zinc-500 text-xs max-w-xs truncate">{promo.notes || "—"}</td>
      <td className="p-3 text-zinc-500 text-xs">
        {promo.created_at ? new Date(promo.created_at).toLocaleDateString() : "—"}
      </td>
      <td className="p-3">
        <div className="flex gap-1">
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7 text-amber-400 hover:bg-amber-500/10"
            onClick={() => onToggle(promo.code)}
            data-testid={`toggle-promo-${promo.code}`}
            title={promo.active ? "Deactivate" : "Activate"}
          >
            <Power className="w-4 h-4" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7 text-red-400 hover:bg-red-500/10 disabled:opacity-30"
            onClick={() => onDelete(promo.code)}
            disabled={promo.used_count > 0}
            data-testid={`delete-promo-${promo.code}`}
            title={promo.used_count > 0 ? "Cannot delete — already used. Deactivate instead." : "Delete"}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </td>
    </tr>
  );
}

export function AdminPromoCodesTab({ getAuthHeader }) {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  // Manual create form
  const [code, setCode] = useState("");
  const [trialDays, setTrialDays] = useState("30");
  const [maxUses, setMaxUses] = useState("");
  const [notes, setNotes] = useState("");

  // Quick generate form
  const [genPrefix, setGenPrefix] = useState("");
  const [genTrialDays, setGenTrialDays] = useState("30");
  const [lastGenerated, setLastGenerated] = useState(null);

  const fetchPromos = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/promo/admin/list", { headers: getAuthHeader() });
      setPromos(res.data.promos || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load promos");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchPromos();
  }, [fetchPromos]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!code.trim()) return toast.error("Code required");
    setCreating(true);
    try {
      await api.post(
        "/promo/admin/create",
        {
          code: code.trim(),
          trial_days: parseInt(trialDays, 10) || 30,
          max_uses: maxUses ? parseInt(maxUses, 10) : null,
          notes,
        },
        { headers: getAuthHeader() }
      );
      toast.success(`Created: ${code.toUpperCase()}`);
      setCode(""); setMaxUses(""); setNotes("");
      fetchPromos();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create");
    } finally {
      setCreating(false);
    }
  };

  const handleGenerate = async () => {
    setCreating(true);
    try {
      const res = await api.post(
        "/promo/admin/generate",
        {
          trial_days: parseInt(genTrialDays, 10) || 30,
          max_uses: 1, // single-use for random codes
          prefix: genPrefix.trim(),
          notes: `Auto-generated ${new Date().toLocaleDateString()}`,
        },
        { headers: getAuthHeader() }
      );
      setLastGenerated(res.data.code);
      copyToClipboard(res.data.code);
      toast.success(`Generated & copied: ${res.data.code}`);
      fetchPromos();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate");
    } finally {
      setCreating(false);
    }
  };

  const handleToggle = async (targetCode) => {
    try {
      await api.put(`/promo/admin/${targetCode}/toggle`, {}, { headers: getAuthHeader() });
      toast.success("Toggled");
      fetchPromos();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to toggle");
    }
  };

  const handleDelete = async (targetCode) => {
    if (!window.confirm(`Delete promo ${targetCode}? This cannot be undone.`)) return;
    try {
      await api.delete(`/promo/admin/${targetCode}`, { headers: getAuthHeader() });
      toast.success("Deleted");
      fetchPromos();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-promo-tab">
      {/* Quick Generate */}
      <Card className="bg-gradient-to-br from-violet-500/10 via-cyan-500/5 to-transparent border-violet-500/30">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-violet-400" />
            Quick Generate
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-zinc-400 mb-4">
            Instantly create a random single-use code (e.g., <code className="text-cyan-400">XK4M2H7P</code>).
            Copied to clipboard automatically. Share it with one specific person.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-zinc-400 text-xs">Prefix (optional)</Label>
              <Input
                value={genPrefix}
                onChange={(e) => setGenPrefix(e.target.value.toUpperCase())}
                placeholder="e.g., VIP"
                className="bg-zinc-900 border-zinc-800 font-mono"
                data-testid="promo-gen-prefix"
              />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs">Trial days</Label>
              <Input
                type="number"
                value={genTrialDays}
                onChange={(e) => setGenTrialDays(e.target.value)}
                className="bg-zinc-900 border-zinc-800"
                data-testid="promo-gen-days"
              />
            </div>
            <div className="md:col-span-2 flex items-end">
              <Button
                onClick={handleGenerate}
                disabled={creating}
                className="w-full bg-gradient-to-r from-violet-500 to-cyan-500 text-white font-bold"
                data-testid="promo-gen-btn"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Generate & Copy
              </Button>
            </div>
          </div>
          {lastGenerated && (
            <div
              className="mt-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg flex items-center justify-between"
              data-testid="promo-gen-last"
            >
              <div>
                <p className="text-xs text-zinc-500">Last generated (copied to clipboard):</p>
                <code className="text-lg font-mono text-green-400 font-bold">{lastGenerated}</code>
              </div>
              <Button size="sm" variant="outline" onClick={() => copyToClipboard(lastGenerated)}>
                <Copy className="w-3 h-3 mr-2" />Copy again
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Manual Create */}
      <Card className="bg-zinc-950/50 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Plus className="w-5 h-5 text-cyan-400" />
            Create Custom Code
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div>
              <Label className="text-zinc-400 text-xs">Code *</Label>
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="ANDREVIP"
                className="bg-zinc-900 border-zinc-800 font-mono"
                data-testid="promo-create-code"
                required
              />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs">Trial days</Label>
              <Input
                type="number"
                value={trialDays}
                onChange={(e) => setTrialDays(e.target.value)}
                className="bg-zinc-900 border-zinc-800"
                data-testid="promo-create-days"
              />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs">Max uses (blank = unlimited)</Label>
              <Input
                type="number"
                value={maxUses}
                onChange={(e) => setMaxUses(e.target.value)}
                placeholder="e.g., 50"
                className="bg-zinc-900 border-zinc-800"
                data-testid="promo-create-max"
              />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs">Notes</Label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Reason / campaign"
                className="bg-zinc-900 border-zinc-800"
                data-testid="promo-create-notes"
              />
            </div>
            <div className="flex items-end">
              <Button
                type="submit"
                disabled={creating}
                className="w-full btn-primary"
                data-testid="promo-create-btn"
              >
                <Plus className="w-4 h-4 mr-2" />Create
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* List */}
      <Card className="bg-zinc-950/50 border-zinc-800">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-white flex items-center gap-2">
            <Gift className="w-5 h-5 text-amber-400" />
            All Promo Codes ({promos.length})
          </CardTitle>
          <Button size="sm" variant="outline" onClick={fetchPromos} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 flex justify-center"><div className="spinner" /></div>
          ) : promos.length === 0 ? (
            <div className="p-12 text-center text-zinc-500">No promo codes yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="promo-list-table">
                <thead className="bg-zinc-900/80 text-zinc-400 text-xs uppercase">
                  <tr>
                    <th className="text-left p-3">Code</th>
                    <th className="text-left p-3">Trial</th>
                    <th className="text-left p-3">Uses</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-left p-3">Notes</th>
                    <th className="text-left p-3">Created</th>
                    <th className="text-left p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {promos.map((p) => (
                    <PromoRow key={p.code} promo={p} onToggle={handleToggle} onDelete={handleDelete} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
